/**
 * As rotas e os endereços — a parte PURA do roteador.
 *
 * Separada de `rotas.ts` (09/10/2026) para poder ser testada sem navegador: `rotas.ts`
 * mexe em `window.history` e ouve `popstate`; isto aqui só traduz rota ⇄ endereço. Quem
 * importa continua importando de `rotas.ts`, que reexporta tudo.
 */

export type Rota =
  | { nome: 'entrar' }
  | { nome: 'incorporadoras' }
  | { nome: 'empreendimentos'; empresaId: string }
  /**
   * O EMPREENDIMENTO aberto. Não é mais uma tela de lista (lote de 09/10/2026, item 4 —
   * "Elimine a tela intermediária que existe com os cenários listados"): o endereço
   * continua existindo para os links e favoritos antigos, mas RESOLVE — abre o cenário
   * padrão (o último aberto neste navegador, senão o mais antigo) trocando o endereço no
   * histórico, ou mostra o estado vazio quando o empreendimento não tem cenário ativo.
   */
  | { nome: 'empreendimento'; empresaId: string; empreendimentoId: string }
  /**
   * Área de Documentos — as pastas dos empreendimentos da incorporadora.
   *
   * `empresaId: null` é o caso em que o aplicativo ainda NÃO SABE de quem são as pastas
   * (o item da barra lateral foi clicado logo depois de entrar, sem nenhuma incorporadora
   * aberta). A tela resolve: com uma só incorporadora, entra nela sozinha; com várias,
   * pergunta uma vez. Sem este estado, o item da barra teria de ficar desabilitado às
   * vezes — e item que às vezes não funciona é pior que uma pergunta.
   */
  | { nome: 'documentos'; empresaId: string | null }
  | { nome: 'cenario'; cenarioId: string }
  | { nome: 'equipe'; empresaId: string }
  /* Fila de pedidos de exclusão — só o Admin Hype chega aqui, e quem barra é o banco. */
  | { nome: 'pedidos' }
  /* A ÚNICA rota que vale sem sessão. O convidado ainda não tem conta. */
  | { nome: 'convite' };

/** O endereço atual, sem depender da lib DOM (este arquivo também roda nos testes). */
function enderecoAtual(): string {
  const local = (globalThis as unknown as { location?: { pathname: string; search: string } }).location;
  return local === undefined ? '/' : local.pathname + local.search;
}

export function caminhoDe(r: Rota): string {
  switch (r.nome) {
    case 'entrar': return '/entrar';
    case 'incorporadoras': return '/';
    case 'empreendimentos': return `/inc/${r.empresaId}`;
    case 'empreendimento': return `/inc/${r.empresaId}/emp/${r.empreendimentoId}`;
    case 'documentos': return r.empresaId === null ? '/documentos' : `/inc/${r.empresaId}/documentos`;
    case 'cenario': return `/cenario/${r.cenarioId}`;
    case 'equipe': return `/inc/${r.empresaId}/equipe`;
    case 'pedidos': return '/pedidos';
    /* O caminho real do convite carrega token e e-mail; quem monta é a tela de Equipe.
       Aqui só existe o destino genérico, para o roteador saber voltar a ele. */
    case 'convite': return enderecoAtual();
  }
}

/** Endereço desconhecido cai em `incorporadoras` — é a raiz de tudo depois de entrar. */
export function rotaDe(caminho: string): Rota {
  const p = caminho.split('?')[0]!.split('/').filter((x) => x !== '');
  if (p[0] === 'entrar') return { nome: 'entrar' };
  if (p[0] === 'pedidos') return { nome: 'pedidos' };
  if (p[0] === 'cenario' && p[1] !== undefined) return { nome: 'cenario', cenarioId: p[1] };
  if (p[0] === 'convite' && p[1] !== undefined) return { nome: 'convite' };
  if (p[0] === 'documentos') return { nome: 'documentos', empresaId: null };
  if (p[0] === 'inc' && p[1] !== undefined) {
    if (p[2] === 'equipe') return { nome: 'equipe', empresaId: p[1] };
    if (p[2] === 'documentos') return { nome: 'documentos', empresaId: p[1] };
    if (p[2] === 'emp' && p[3] !== undefined) {
      return { nome: 'empreendimento', empresaId: p[1], empreendimentoId: p[3] };
    }
    return { nome: 'empreendimentos', empresaId: p[1] };
  }
  return { nome: 'incorporadoras' };
}
